import {
    createContext,
    type PropsWithChildren,
    useContext,
    useEffect,
    useRef,
    useSyncExternalStore
} from "react";

import {
    createDinoContextTopic,
    dinoContextTopic,
    type DinoContextTopic,
    type DinoTutorContextPatch
} from "./dinoContextTopic.js";

const DinoContextTopicContext = createContext<DinoContextTopic | null>(null);

export interface DinoContextProviderProps extends PropsWithChildren {
    /** Supply a topic in tests or when an application needs an isolated scope. */
    readonly topic?: DinoContextTopic;
}

export function DinoContextProvider({
    children,
    topic
}: DinoContextProviderProps) {
    const localTopic = useRef<DinoContextTopic>(
        topic ?? createDinoContextTopic()
    );

    return (
        <DinoContextTopicContext.Provider value={localTopic.current}>
            {children}
        </DinoContextTopicContext.Provider>
    );
}

export function useDinoContextTopic(): DinoContextTopic {
    return useContext(DinoContextTopicContext) ?? dinoContextTopic;
}

export function useDinoContext() {
    const topic = useDinoContextTopic();

    return useSyncExternalStore(
        notify => topic.subscribe(() => notify()),
        topic.getSnapshot,
        topic.getSnapshot
    );
}

export function usePublishDinoContext(
    source: string,
    patch: DinoTutorContextPatch | null
): void {
    const topic = useDinoContextTopic();

    useEffect(
        () => {
            if (patch === null) {
                topic.removeSource(source);
                return undefined;
            }

            return topic.publish(
                patch,
                source
            );
        },
        [
            patch,
            source,
            topic
        ]
    );
}
